/**
 * @module components/common/NotifyConfirmDialog
 * @description Diálogo de confirmación con opción de notificar al usuario afectado.
 *
 * Variante del `ConfirmDialog` para acciones que afectan a otro usuario (asignación,
 * actualización de tarea, etc.). Incluye dos botones mutuamente excluyentes:
 * - "Confirmar y Notificar": ejecuta la acción y envía notificación al usuario
 * - "Confirmar sin Notificar": ejecuta la acción silenciosamente
 *
 * El callback `onConfirm(notify: boolean)` indica si se debe notificar.
 *
 * @see DataContext para las funciones que usan este diálogo (assignControlToReferent, etc.)
 */
import React from 'react';
import { Bell, BellOff, X } from 'lucide-react';

interface NotifyConfirmDialogProps {
  recipientName: string;
  actionLabel: string;
  onConfirm: (notify: boolean) => void;
  onCancel: () => void;
}

export function NotifyConfirmDialog({
  recipientName,
  actionLabel,
  onConfirm,
  onCancel,
}: NotifyConfirmDialogProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm">
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              ¿Notificar a {recipientName}?
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Se enviará un correo a <span className="font-medium text-gray-700 dark:text-gray-300">{recipientName}</span> al {actionLabel}.
          </p>

          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={() => onConfirm(false)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <BellOff className="w-4 h-4" />
              No, solo guardar
            </button>
            <button
              onClick={() => onConfirm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Bell className="w-4 h-4" />
              Sí, notificar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
